import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';

import { environment } from 'src/environments/environment';

import { PreAuthRequest } from '../models/pre-auth/pre-auth-request.model';
import { PreAuthResponse } from '../models/pre-auth/pre-auth-response.model';
import { PickupData } from '../models/pickup/pickup-data.model';
import { PickupResponse } from '../models/pickup/pickup-response.model';
import { PickupDateHoursPeriodResponse } from '../models/pickup-date-hours-period/pickup-date-hours-period-response.model';
import { LabelRequest } from '../models/label/label-request.model';
import { LabelResponse } from '../models/label/label-response.model';
import { CaptchaResponse } from '../models/main-page/captcha-response.model';
import { CityZipCodeRequest } from '../models/zip-code/city-zip-code-request.model';

import { StoreService } from './store.service';
import { PointDto } from '../models/fds2-points/fds2-points.model';

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  private apiUrl = `${environment.apiUrl}`;
  private znrpoApiUrl = `${environment.znrpoApiUrl}`;

  constructor(private http: HttpClient, private storeService: StoreService) {}

  // Captcha
  submitCaptchaRequest(): Observable<CaptchaResponse> {
    const captchaUrl = `${this.apiUrl}/captha`;
    return this.http.get<CaptchaResponse>(captchaUrl);
  }

  // Pre-Authorization
  submitPreAuthRequest(
    preAuthRequest: PreAuthRequest
  ): Observable<PreAuthResponse> {
    const preAuthUrl = `${this.apiUrl}/preAuth`;
    return this.http.post<PreAuthResponse>(preAuthUrl, preAuthRequest);
  }

  // Pickup
  submitPickupRequest(pickupData: PickupData): Observable<PickupResponse> {
    const tokenJwt = this.storeService.getTokenJwt();
    const headers = new HttpHeaders().set(
      'Authorization',
      `Bearer ${tokenJwt}`
    );
    const pickupUrl = `${this.apiUrl}/pickup`;
    return this.http.post<PickupResponse>(pickupUrl, pickupData, { headers });
  }

  // PickupDateHours
  getPickupDateHoursPeriod(
    postalCode: string,
    pickupDate: string,
    paletteAmount: number
  ): Observable<PickupDateHoursPeriodResponse> {
    const tokenJwt = this.storeService.getTokenJwt();
    const headers = new HttpHeaders().set(
      'Authorization',
      `Bearer ${tokenJwt}`
    );
    const pickupDateHoursPeriodUrl = `${this.apiUrl}/pickupDateHoursPeriod?postalCode=${postalCode}&pickupDate=${pickupDate}&palleteAmount=${paletteAmount}`;
    return this.http.get<PickupDateHoursPeriodResponse>(
      pickupDateHoursPeriodUrl,
      { headers }
    );
  }

  // Labels
  submitLabelRequest(labelRequest: LabelRequest): Observable<LabelResponse> {
    const tokenJwt = this.storeService.getTokenJwt();
    const headers = new HttpHeaders().set(
      'Authorization',
      `Bearer ${tokenJwt}`
    );
    const labelUrl = `${this.apiUrl}/label`;
    return this.http.put<LabelResponse>(labelUrl, labelRequest, { headers });
  }

  // City and Zip Code
  submitCityZipCodeRequest(
    cityZipCodeRequest: CityZipCodeRequest
  ): Observable<string> {
    const tokenJwt = this.storeService.getTokenJwt();
    const headers = new HttpHeaders().set(
      'Authorization',
      `Bearer ${tokenJwt}`
    );
    const cityZipCodeUrl = `${this.apiUrl}/cityzipcode`;
    return this.http.put<string>(cityZipCodeUrl, cityZipCodeRequest, {
      headers,
    });
  }

  // Retail Points from FDS2 API
  getPoints(searchKey: string, page: number): Observable<PointDto[]> {
    const tokenJwt = this.storeService.getPointsAuthToken();
    const headers = new HttpHeaders().set(
      'Authorization',
      `Bearer ${tokenJwt}`
    );
    //    const pointsUrl = `${this.znrpoApiUrl}/points/address?partner=ABC&partner=DELIKATESY%20CENTRUM&partner=DU?Y%20BEN&partner=EPAKA&partner=FURGONETKA&partner=GROSZEK&partner=KOLPORTER&partner=PP_TEST&partner=SHELL&partner=STOKROTKA&search=${searchKey}&limit=10&page=${page}`;
    const pointsParams = this.buildPointsParams();
    const pointsUrl = `${this.znrpoApiUrl}/points/address?${pointsParams}&search=${searchKey}&limit=10&page=${page}`;
    return this.http.get<PointDto[]>(pointsUrl, { headers });
  }

  private partnerQueryMap: { [key: string]: string } = {
    ABC: 'ABC',
    DELIKATESY: 'DELIKATESY%20CENTRUM',
    DUZYBEN: 'DU?Y%20BEN',
    EPAKA: 'EPAKA',
    FURGONETKA: 'FURGONETKA',
    GROSZEK: 'GROSZEK',
    KOLPORTER: 'KOLPORTER',
    SHELL: 'SHELL',
    STOKROTKA: 'STOKROTKA',
  };

  buildPointsParams(): string {
    const selectedPartners = this.storeService.getSelectedPartners();

    if (!selectedPartners || selectedPartners.length === 0) {
      return 'partner=ABC&partner=DELIKATESY%20CENTRUM&partner=DU?Y%20BEN&partner=EPAKA&partner=FURGONETKA&partner=GROSZEK&partner=KOLPORTER&partner=SHELL&partner=STOKROTKA';
    }

    const queryParts = selectedPartners
      .map((partner) => this.partnerQueryMap[partner] || partner)
      .map((mappedPartner) => `partner=${mappedPartner}`);

    return `${queryParts.join('&')}`;
  }
}
